import { useMutation } from '@apollo/client/react';
import { HistoryIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/Dialog';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  SUBMIT_CUSTOMER_FEEDBACK,
  UPLOAD_FEEDBACK_FILE
} from '@/graphql/customerFeedback';
import { formatFileSize } from '@/lib/utils';

interface CustomerFeedbackDialogProps {
  onOpenChange: (open: boolean) => void;
  onSubmitSuccess?: () => void;
  open: boolean;
  userEmail?: string;
  userName?: string;
}

export function CustomerFeedbackDialog({
  onOpenChange,
  onSubmitSuccess,
  open,
  userEmail,
  userName
}: CustomerFeedbackDialogProps) {
  const location = useLocation();
  const [submitFeedback] = useMutation(SUBMIT_CUSTOMER_FEEDBACK);
  const [uploadFile] = useMutation(UPLOAD_FEEDBACK_FILE);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isImportant, setIsImportant] = useState(false);
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    // Validate each file
    const validFiles: File[] = [];
    for (const file of files) {
      // Check file size (max 50MB per file)
      if (file.size > 50 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 50MB)`);
        continue;
      }
      validFiles.push(file);
    }

    // Add to existing files (max 5 total)
    const newFiles = [...attachmentFiles, ...validFiles].slice(0, 5);
    setAttachmentFiles(newFiles);

    if (newFiles.length >= 5 && validFiles.length > 0) {
      toast.info('Maximum 5 files allowed');
    }

    // Reset input
    e.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setAttachmentFiles(attachmentFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error('Please provide a title for your feedback');
      return;
    }

    setIsSubmitting(true);

    try {
      const odooBaseUrl = import.meta.env.VITE_ODOO_BASE_URL;

      // Capture context information
      const currentUrl = window.location.href;

      const uploadedFileUrls: string[] = [];

      // Step 1: Upload files to Linear if provided
      if (attachmentFiles.length > 0) {
        setIsUploadingFile(true);

        for (let i = 0; i < attachmentFiles.length; i++) {
          const file = attachmentFiles[i];
          setUploadProgress(`Uploading ${i + 1}/${attachmentFiles.length}...`);

          const formData = new FormData();
          formData.append('file', file);
          formData.append(
            'query',
            'mutation { uploadFeedbackFile { success error url } }'
          );

          try {
            const uploadResponse = await fetch(`${odooBaseUrl}/api/graphql`, {
              body: formData,
              credentials: 'include',
              method: 'POST'
            });

            if (uploadResponse.ok) {
              const uploadResult = await uploadResponse.json();
              const uploadData = uploadResult.data?.uploadFeedbackFile;

              if (uploadData?.success && uploadData.url) {
                uploadedFileUrls.push(uploadData.url);
              } else {
                const errorMsg = uploadData?.error || 'Failed to upload file';
                console.error('Upload response:', uploadResult);
                toast.error(`${file.name}: ${errorMsg}`);
                setIsSubmitting(false);
                setIsUploadingFile(false);
                setUploadProgress('');
                return;
              }
            } else {
              const errorText = await uploadResponse.text();
              console.error('Upload failed:', errorText);
              toast.error(`Failed to upload ${file.name}`);
              setIsSubmitting(false);
              setIsUploadingFile(false);
              setUploadProgress('');
              return;
            }
          } catch (error) {
            console.error('Error uploading file:', error);
            toast.error(`Error uploading ${file.name}`);
            setIsSubmitting(false);
            setIsUploadingFile(false);
            setUploadProgress('');
            return;
          }
        }

        setIsUploadingFile(false);
        setUploadProgress('');
      }

      // Step 2: Submit the feedback via GraphQL
      const { data } = await submitFeedback({
        variables: {
          attachmentUrls: uploadedFileUrls,
          currentUrl: currentUrl,
          description: description || '',
          priority: isImportant ? 1 : 0,
          title: title.trim()
        }
      });

      const result = data?.submitCustomerFeedback;

      if (result?.success) {
        const issue = result.issue;
        toast.success(`Feedback submitted successfully! (${issue.identifier})`);

        // Call success callback if provided (e.g., to refresh data)
        if (onSubmitSuccess) {
          onSubmitSuccess();
        }

        // Reset form
        setTitle('');
        setDescription('');
        setIsImportant(false);
        setAttachmentFiles([]);
        setUploadProgress('');
        onOpenChange(false);
      } else if (result?.error) {
        toast.error(result.error);
      } else {
        toast.error('Failed to submit feedback. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('An error occurred while submitting feedback.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open={open}
    >
      <DialogContent
        aria-describedby={undefined}
        className="sm:max-w-[540px]"
      >
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
          <DialogDescription>
            Share your thoughts, report issues, or suggest improvements
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="grid gap-4">
            <Field>
              <FieldLabel htmlFor="title">Subject</FieldLabel>
              <Input
                autoFocus
                disabled={isSubmitting}
                id="title"
                onChange={e => {
                  setTitle(e.target.value);
                }}
                placeholder="Brief summary of your feedback"
                value={title}
              />
            </Field>

            <Field>
              <FieldLabel htmlFor="description">Message</FieldLabel>
              <Textarea
                className="min-h-32 resize-none"
                disabled={isSubmitting}
                id="description"
                onChange={e => {
                  setDescription(e.target.value);
                }}
                placeholder="Describe your feedback in detail..."
                value={description}
              />
            </Field>

            {attachmentFiles.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm">Attachments</Label>
                {attachmentFiles.map((file, index) => (
                  <div
                    className="flex items-center gap-2 rounded-md border p-2 text-sm"
                    key={index}
                  >
                    <span className="flex-1 truncate">{file.name}</span>
                    <span className="text-muted-foreground text-xs">
                      {formatFileSize(file.size, { maxDecimals: 1 })}
                    </span>
                    <Button
                      className="h-8 px-2"
                      disabled={isSubmitting}
                      onClick={() => {
                        handleRemoveFile(index);
                      }}
                      size="sm"
                      type="button"
                      variant="ghost"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {attachmentFiles.length < 5 && (
              <div>
                <Input
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  className="cursor-pointer"
                  disabled={isSubmitting}
                  id="attachment"
                  multiple
                  onChange={handleFileChange}
                  type="file"
                />
                <p className="text-muted-foreground mt-1.5 text-xs">
                  Attach screenshots or documents (max 50MB each, 5 files)
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 rounded-lg border p-3">
              <Switch
                checked={isImportant}
                disabled={isSubmitting}
                id="important"
                onCheckedChange={setIsImportant}
              />
              <div className="flex-1">
                <Label
                  className="cursor-pointer font-medium"
                  htmlFor="important"
                >
                  Mark as important
                </Label>
                <p className="text-muted-foreground text-xs">
                  Flag this feedback for immediate attention
                </p>
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="sm:justify-between">
          <Button
            asChild
            size="sm"
            variant="ghost"
          >
            <Link
              onClick={() => {
                onOpenChange(false);
              }}
              to="/customer-requests"
            >
              <HistoryIcon />
              View All Feedback
            </Link>
          </Button>
          <div className="flex gap-2">
            <Button
              disabled={isSubmitting}
              onClick={() => {
                onOpenChange(false);
              }}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isSubmitting || !title.trim()}
              onClick={handleSubmit}
            >
              {uploadProgress || (isSubmitting ? 'Submitting...' : 'Send')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
